import React, { FC, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import { AxiosError } from 'axios';
import {
  APIGatewayResponse,
  APIGatewayResponseDataHeader,
} from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { useTranslation } from 'react-i18next';
import { Grid, Typography, Box, makeStyles } from '@dxb-web-ui-framework/design-system';
import { FormBuilderPresenter } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/FormBuilderPresenter';
import { invokeService, useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks';
import { useCorporateCustomerId, useSelectedCustomerId } from '../../../../../userProfile/src/contexts/UserInfo/hooks';
import { ErrorAlert } from '../../../../../content-composer/src/components/Generic/ErrorAlert/ErrorAlert';
import { ContentWrapper } from '../../../../../content-composer/src/components/Generic/ContentWrapper';
import { getRoutePath } from '../../../../../content-composer/src/components/Routes/routePaths';
import { ErrorContent } from '../../../../../content-composer/src/components/Partials/ErrorContent';
import { ForecastResponseBody } from '../../../domain/Transfer/entities/ForecastResponseDTO';
import { useSelectedFavouriteTransfer } from '../../../contexts/FavouriteTransfers/hooks';
import { TransferServiceImpl } from '../../../domain/Transfer/usecases';
import { Forecast, ForecastRouting, SwiftCode } from '../../../domain/Transfer/entities';
import { Country } from '../../../domain/Country/entities';
import {
  useWizardGoToNextStep,
  useWizardStepData,
} from '../../../../../content-composer/src/contexts/ClientSideWizard/hooks';
import { constructForecastRequestObjectByTransferType, preloadedErrorsSetter } from '../../../utils';
import { Priorities, ValueDateResponseBody } from '../../../domain/Transfer/entities/ValueDateResponseDTO';
import { RoutingTransferTypes } from '../../../domain/Transfer/entities/RoutingResponseDTO';
import { ForecastRoutingResponseBody } from '../../../domain/Transfer/entities/ForecastRoutingResponseDTO';
import { ForeCastFormData, UnknownBankDetails, ValueIdentifier } from '../../types';
import { AddBankDetailsDrawer } from '../AddBankDetailsDrawer';
import { getFormConfigsByTransferType } from './getFormConfigsByTransferType';
import { useBopAndVocCodesEndpointsData } from './useBopAndVocCodesEndpointsData';

type InitTransferStepProps = {
  toggleStepperIsActive: Function;
  service: TransferServiceImpl;
};

const useStyles = makeStyles({
  errorContainer: {
    wordBreak: 'break-all',
  },
});

const InitTransferStep: FC<InitTransferStepProps> = ({ toggleStepperIsActive, service }) => {
  const favouriteTransfer = useSelectedFavouriteTransfer();
  const formRef = useRef<HTMLFormElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [unknownBankDetails, setUnknownBankDetails] = useState<UnknownBankDetails>(null);
  const [selectedBankSwiftCode, setSelectedBankSwiftCode] = useState<Nullable<SwiftCode>>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [preloadedErrors, setPreloadedErrors] = useState<any[]>([]);
  const [formData, setFormData] = useState<Nullable<ForeCastFormData & Forecast>>(null);
  const [bicQuery, setBicQuery] = useState<Nullable<string>>(null);
  const [postDataError, setPostDataError] = useState<
    AxiosError<APIGatewayResponse<ForecastResponseBody>, any> | undefined
  >(undefined);
  const [valueDatePostError, setValueDatePostError] = useState<
    AxiosError<APIGatewayResponse<ValueDateResponseBody>, any> | undefined
  >(undefined);
  const [chargingMethod, setChargingMethod] = useState<Nullable<{ value: number; description: string }>>(null);
  const [timestamp, setTimestamp] = useState<Nullable<string>>(null);
  // hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const companyId = useCorporateCustomerId();
  const selectedCustomerId = useSelectedCustomerId();

  // data from hooks
  const goToNextWizardStep = useWizardGoToNextStep();
  const initTransferData = useWizardStepData('initTransfer') as { values: GenericObject };
  // transfer type check
  const transferType = initTransferData.values.routingResponseData.transferType;
  const forecastRoutingData = initTransferData.values?.forecastRoutingResponseData as Nullable<ForecastRouting>;
  const isExternalUnknownBankTransferType =
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_OTHER_CURRENCY;

  const isExternalTransfer =
    transferType === RoutingTransferTypes.EXTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_OTHER_CURRENCY;

  const { bopCodesData, isLoadingBopCodes, vocCodesData, isLoadingVocCodes, error, shouldShowErrorContent } =
    useBopAndVocCodesEndpointsData(
      service,
      initTransferData.values.routingResponseData?.bankCountry || formRef?.current?.getFormData()?.bankCountry,
      transferType,
      initTransferData.values.otherMandatoryDataForUI.countries,
      initTransferData.values.routingFormData.orderCurrency,
      selectedBankSwiftCode,
    );

  const {
    data: forecastRoutingUpdatedData,
    error: forecastRoutingError,
    isLoading: isLoadingForecastRouting,
  } = useInvokeSWRService<ForecastRoutingResponseBody>(
    service?.ForecastRouting,
    {
      debitAccount: initTransferData.values.routingFormData.debitAccountNumber,
      creditAccount: initTransferData.values.routingFormData.creditAccountNumber,
      bic: Boolean(initTransferData.values.routingResponseData.bic)
        ? initTransferData.values.routingResponseData.bic
        : formData?.receiverBic,
      amount: Number(initTransferData.values.routingFormData.orderAmount),
      currency: Number(initTransferData.values.routingFormData.orderCurrency),
      charges: chargingMethod?.value,
      chargesDescription: chargingMethod?.description,
      meta: {
        timestamp,
      },
    },
    Boolean(chargingMethod) && Boolean(timestamp),
    'transferService.Routing',
  );

  const postStateHandler = (dataFromForm: any, requestObject: any) => {
    unstable_batchedUpdates(() => {
      setFormData({ ...dataFromForm, ...requestObject });
      setIsLoading(true);
      setPostDataError(undefined);
      setPreloadedErrors([]);
    });
  };

  const postFieldErrorHandler = (fieldResponseErrors?: APIGatewayResponseDataHeader) => {
    unstable_batchedUpdates(() => {
      preloadedErrorsSetter(setPreloadedErrors, fieldResponseErrors);
      setIsLoading(false);
    });
    return;
  };

  const forecastErrorHandler = (error: any) => {
    unstable_batchedUpdates(() => {
      setPostDataError(error);
      setIsLoading(false);
    });
  };

  const postForecastSuccessHandler = (
    response: GenericObject,
    dataFromForm: any,
    requestObject: any,
    bankCountry: Country,
    beneficiaryCountry: Country,
  ) => {
    setIsLoading(false);
    goToNextWizardStep({
      forecastResponseData: response.data,
      forecastRoutingUpdatedData:
        forecastRoutingUpdatedData !== undefined
          ? forecastRoutingUpdatedData
          : initTransferData?.values?.forecastRoutingUpdatedData !== undefined
          ? initTransferData?.values?.forecastRoutingUpdatedData
          : undefined,
      forecastFormData: { ...requestObject, ...dataFromForm, selectedCountry: beneficiaryCountry },
      bankCountry,
      beneficiaryCountry,
      bicQuery,
      unknownBankDetails,
      selectedBankSwiftCode,
    });
  };

  const onSubmitAction = async (dataFromForm: ForeCastFormData) => {
    const defaultVocCodeData = vocCodesData ? vocCodesData[0] : undefined;
    const countries = (initTransferData.values.otherMandatoryDataForUI.countries as Country[]).filter(
      c => c.serialNumber !== 0,
    );

    const shouldConsiderBankCountryAsBeneficiaryCountry =
      transferType === RoutingTransferTypes.INTRABANK_SAME_CURRENCY ||
      transferType === RoutingTransferTypes.INTERNAL_SAME_CURRENCY ||
      transferType === RoutingTransferTypes.CORPORATE_INTERNAL_SAME_CURRENCY ||
      transferType === RoutingTransferTypes.INTERNAL_OTHER_CURRENCY ||
      transferType === RoutingTransferTypes.INTRABANK_OTHER_CURRENCY ||
      transferType === RoutingTransferTypes.CORPORATE_INTERNAL_OTHER_CURRENCY;

    const bankCountry = countries.find(country => {
      if (initTransferData.values.routingResponseData?.bic) {
        return country.shortDescription === initTransferData.values.routingResponseData?.bic?.substring(4, 6);
      }
      if (dataFromForm?.receiverBic) {
        return country.shortDescription === dataFromForm?.receiverBic?.substring(4, 6);
      }
      if (favouriteTransfer && !selectedBankSwiftCode?.country) {
        return country.serialNumber === favouriteTransfer?.bankCountrySn;
      }
      return country.shortDescription === selectedBankSwiftCode?.bic?.substring(4, 6);
    }) as Country;

    const beneficiaryCountry = countries.find(country => {
      if (shouldConsiderBankCountryAsBeneficiaryCountry) {
        return country.description === initTransferData.values.routingResponseData?.bankCountry;
      }
      return country.serialNumber === dataFromForm?.receiverCountry?.id;
    }) as Country;

    // First we get the transfer date
    const priorityDate = !dataFromForm?.priority
      ? Priorities.IMMEDIATE
      : (dataFromForm.priority.toUpperCase() as Priorities);
    setIsLoading(true);
    const {
      data: orderValueDate,
      isLoading,
      error: valueDateError,
    } = await invokeService<ValueDateResponseBody>(() =>
      service.GetTransferValueDate({
        priorityDate,
      }),
    );
    setIsLoading(false);
    if (valueDateError && !isLoading) {
      setValueDatePostError(valueDateError);
      return;
    }

    //if the value date is available then the request payload starts to construct and execute forecast
    if (orderValueDate && !isLoading) {
      const requestObject = constructForecastRequestObjectByTransferType(
        initTransferData.values.routingResponseData?.bankName ||
          selectedBankSwiftCode?.bankDescr ||
          dataFromForm?.bankName,
        priorityDate,
        orderValueDate.dateTime,
        initTransferData.values,
        //@ts-ignore
        !Boolean(dataFromForm.chargingMethod) && (isExternalTransfer || isExternalUnknownBankTransferType)
          ? formData
          : dataFromForm,
        bankCountry,
        beneficiaryCountry,
        initTransferData.values.otherMandatoryDataForUI.chargingMethods,
        defaultVocCodeData?.shortDescription,
        forecastRoutingUpdatedData
          ? forecastRoutingUpdatedData?.routingSystem
          : initTransferData?.values?.forecastRoutingUpdatedData?.routingSystem
          ? initTransferData?.values?.forecastRoutingUpdatedData?.routingSystem
          : forecastRoutingData?.routingSystem,
      );
      postStateHandler(dataFromForm, requestObject);
      const { error } = await invokeService<ForecastResponseBody>(() =>
        service.Forecast(requestObject, (response: GenericObject) =>
          postForecastSuccessHandler(response, dataFromForm, requestObject, bankCountry, beneficiaryCountry),
        ),
      );
      const isPostFieldDataError = !isEmpty(error?.response?.data.headers[0].param);
      if (isPostFieldDataError && error?.response?.data.headers[0]) {
        postFieldErrorHandler(error?.response?.data.headers[0]);
      }
      if (error) {
        forecastErrorHandler(error);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const onToggleDrawer = () => {
    const values = formRef?.current?.getFormData();
    setFormData(prev => ({ ...prev, ...values }));
    setIsOpen(prev => !prev);
  };

  const onClickCancel = () => {
    if (companyId || selectedCustomerId) {
      navigate(
        getRoutePath({
          routeKey: 'dashboard',
          customerId: (companyId as string) || (selectedCustomerId as string),
        }),
      );
    }
  };

  const getStepTitleByTransferType = () => {
    if (isExternalTransfer || isExternalUnknownBankTransferType) {
      return t('fundsTransferPage.TransferStepper.InitTransfer.Title.Text.External');
    }

    return t('fundsTransferPage.TransferStepper.InitTransfer.Title.Text.Internal');
  };

  useEffect(() => {
    if (
      isExternalUnknownBankTransferType &&
      initTransferData.values?.unknownBankDetails &&
      !isEmpty(initTransferData.values?.unknownBankDetails)
    ) {
      setUnknownBankDetails(initTransferData.values.unknownBankDetails);
    }

    if (
      isExternalUnknownBankTransferType &&
      initTransferData.values?.selectedBankSwiftCode &&
      !isEmpty(initTransferData.values?.selectedBankSwiftCode)
    ) {
      setSelectedBankSwiftCode(initTransferData.values?.selectedBankSwiftCode);
    }

    if (
      isExternalUnknownBankTransferType &&
      initTransferData.values?.bicQuery &&
      !isEmpty(initTransferData.values?.bicQuery)
    ) {
      setBicQuery(initTransferData.values?.bicQuery);
    }
  }, [
    initTransferData.values?.selectedBankSwiftCode,
    initTransferData.values?.unknownBankDetails,
    isExternalUnknownBankTransferType,
    initTransferData.values?.bicQuery,
  ]);

  const onChangeFormData = useCallback(
    (event: any, field: string, value: any) => {
      if ((isExternalTransfer || isExternalUnknownBankTransferType) && field === ValueIdentifier.CHARGING_METHOD) {
        unstable_batchedUpdates(() => {
          setFormData({ ...formRef?.current?.getFormData(), [field]: value });
          setChargingMethod({
            value: Number(value),
            description: initTransferData.values.otherMandatoryDataForUI.chargingMethods.find(
              (chargingMethod: any) => chargingMethod.serialNumber === Number(value),
            ).description,
          });
          setTimestamp(new Date().toISOString());
        });
      }
    },
    [
      initTransferData.values.otherMandatoryDataForUI.chargingMethods,
      isExternalTransfer,
      isExternalUnknownBankTransferType,
    ],
  );

  const shouldShowPriorityFields = useMemo(
    () =>
      Boolean(
        forecastRoutingError || forecastRoutingData === null
          ? true
          : forecastRoutingUpdatedData
          ? forecastRoutingUpdatedData?.routingSystem !== 'SEPA'
          : initTransferData?.values?.forecastRoutingUpdatedData?.routingSystem
          ? initTransferData?.values?.forecastRoutingUpdatedData?.routingSystem !== 'SEPA'
          : forecastRoutingData?.routingSystem !== 'SEPA',
      ),
    [
      forecastRoutingData,
      forecastRoutingError,
      forecastRoutingUpdatedData,
      initTransferData?.values?.forecastRoutingUpdatedData?.routingSystem,
    ],
  );

  const isSepaRoutingSystem = forecastRoutingUpdatedData
    ? forecastRoutingUpdatedData?.routingSystem === 'SEPA'
    : initTransferData?.values?.forecastRoutingUpdatedData?.routingSystem
    ? initTransferData?.values?.forecastRoutingUpdatedData?.routingSystem === 'SEPA'
    : forecastRoutingData?.routingSystem === 'SEPA';

  const classes = useStyles();

  if (shouldShowErrorContent || valueDatePostError) {
    return (
      <ContentWrapper
        hasDivider={false}
        boxProps={{
          paddingLeft: 3,
          paddingRight: 3,
          paddingTop: 4,
          boxShadow: '0 3px 20px #00000033',
          height: '100%',
        }}>
        <Grid container>
          <Grid item xs={12} className={classes.errorContainer}>
            <ErrorContent
              code={error?.code || valueDatePostError?.code || '404'}
              onClickBtn={onClickCancel}
              subtitle={t('fundsTransferPage.FundTransferRoutingForm.ErrorContent.Subtitle')}
              btnLabel={t('fundsTransferPage.FundTransferRoutingForm.ErrorContent.BtnLabel')}
              message={t('fundsTransferPage.FundTransferRoutingForm.ErrorContent.Message')}
            />
          </Grid>
        </Grid>
      </ContentWrapper>
    );
  }

  return (
    <>
      <ContentWrapper
        hasDivider={false}
        boxProps={{
          paddingLeft: 3,
          paddingRight: 3,
          paddingTop: 4,
          boxShadow: '0 3px 20px #00000033',
          height: '100%',
        }}>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant='subtitle1' color='primary'>
              {getStepTitleByTransferType()}
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <FormBuilderPresenter
              formConfig={getFormConfigsByTransferType(
                t,
                initTransferData,
                service,
                formData,
                isLoading,
                selectedBankSwiftCode,
                bopCodesData,
                vocCodesData,
                isLoadingBopCodes,
                isLoadingVocCodes,
                Boolean(companyId),
                favouriteTransfer,
                shouldShowPriorityFields,
                isSepaRoutingSystem
              )}
              externalProps={{ isLoading }}
              submitAction={onSubmitAction}
              externalFieldErrors={preloadedErrors}
              ref={formRef}
              externalEffectCallbacks={{
                onClickCancel: toggleStepperIsActive,
                onChange: onChangeFormData,
                onToggleDrawer,
                setFormData,
              }}>
              {postDataError && !isEmpty(postDataError) && (
                <Box mt={4} className={classes.errorContainer}>
                  <ErrorAlert message={postDataError.message} />
                </Box>
              )}
            </FormBuilderPresenter>
          </Grid>
        </Grid>
      </ContentWrapper>
      {isExternalUnknownBankTransferType && (
        <AddBankDetailsDrawer
          isOpen={isOpen}
          onToggleDrawer={onToggleDrawer}
          t={t}
          unknownBankDetails={unknownBankDetails}
          service={service}
          bicQuery={bicQuery}
          setBicQuery={setBicQuery}
          setUnknownBankDetails={setUnknownBankDetails}
          setSelectedBankSwiftCode={setSelectedBankSwiftCode}
        />
      )}
    </>
  );
};

export { InitTransferStep };
